/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { range } from "../../../utils/lodash";
import useStyles from "./StarRatingRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import Button from "../../shared/Button";
import { ModernStarRatingClearIcon } from "../../shared/ModernStarRatingIcons";
import useQuestion from "../../../hooks/useQuestion";
import {
    getAriaLabelledByFromId,
    getStarRatingGridCellAriaLabelledByIds,
} from "../../../utils/WCAGHelper";
import { useHybridGridSubHeaderAria } from "./HybridGridSubHeaderAriaContext";
import { QuestionType } from "../../../models/enums/QuestionType";
import { translate } from "../../../models/utilities/translationUtilities";
import StarRenderer from "./StarRenderer";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { StarRatingNavigation } from "../../controls/KeyboardNavigation/StarRatingNavigation";
import { useSelector } from "react-redux";
import { StoreState } from "../../../models/states/StoreState";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import { Theme } from "../../../types/Theme";

interface Props extends RendererProps {
    containerRef: React.LegacyRef<HTMLDivElement>;
    setValue(value: number, reset?: boolean): void;
    starWidth?: number;
    starHeight?: number;
    value: number;
    split: number;
    maxValue: number;
    elementId: string;
    isDisabled?: boolean;
}

function getStars(props: Props) {
    const starCount = props.maxValue * (props.split || 1);
    return range(starCount);
}

/** Escape a string for use inside a double-quoted CSS attribute selector value. */
function escapeCssAttributeSelectorValue(value: string): string {
    return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export default function StarRatingRenderer(props: Props) {
    const [hoveredStarValue, setHoveredStarValue] = React.useState(0);
    const pointerDownOnClearRef = React.useRef(false);
    const {
        value,
        setValue,
        elementId,
        split = 1,
        starWidth = 24,
        starHeight = 24,
        isDisabled = false,
        gridRenderedRowIndex,
        gridRenderedColumnIndex,
        gridRendering,
        variable,
        language,
        questionnaire,
        theme: surveyTheme,
    } = props;
    const theme = useTheme();
    const question = useQuestion();
    const modern = shouldUseModernTheme(theme as Theme);
    // Red error state. A StarRatingGrid keys its validation error by the PARENT (grid) question name; a
    // standalone star rating by its own question name — and the Continuous variable name can differ — so
    // match any of the three. `interview` is guarded for unit-test stores.
    const questionInErrorScope = useSelector((state: StoreState) => {
        const errorsByVariableName = state.interview?.validation?.errorsByVariableName;
        if (!errorsByVariableName) {
            return false;
        }
        const keys = [question?.parent?.name, question?.name, variable?.name];
        return keys.some((key) => key != null && (errorsByVariableName[key]?.length ?? 0) > 0);
    });
    // Only UNANSWERED rows turn red (PO/design: "only the empty star rating should be in error"); an
    // answered row (value > 0) stays normal even though its trailing stars are empty.
    const hasMainVariableValidationError = questionInErrorScope && !(value > 0);
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const includeHybridGridSubHeaderChrome = useHybridGridSubHeaderAria();
    const rerenderParentCallback = (input) => {
        setHoveredStarValue(input);
    };
    const ariaAttributes = {};
    const pageQuestion = question?.parent ?? question;

    // Cells always get gridRendering=true from VariableCell. StarRatingGrid was incorrectly using the
    // HybridGrid_STARRATING id scheme (wrong DOM ids). For StarRatingGrid, tie the radiogroup to the
    // section TITLE/LABEL on the page question (parent), not the cell pseudo-question.
    if (gridRendering && pageQuestion?.type === QuestionType.HybridGrid) {
        ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(
            question,
            questionId,
            `${QuestionType.HybridGrid}_STARRATING`,
            gridRenderedColumnIndex + 1,
            gridRenderedRowIndex + 1,
            undefined,
            undefined,
            undefined,
            includeHybridGridSubHeaderChrome,
        );
    } else if (pageQuestion?.type === QuestionType.StarRatingGrid) {
        // Keep radiogroup name scoped to row/column only; question context is provided at table level.
        if (
            typeof gridRenderedRowIndex === "number" &&
            typeof gridRenderedColumnIndex === "number"
        ) {
            const ariaIds = getStarRatingGridCellAriaLabelledByIds(
                pageQuestion,
                language,
                gridRenderedRowIndex,
                gridRenderedColumnIndex,
            );
            if (ariaIds.length > 0) {
                ariaAttributes["aria-labelledby"] = ariaIds.join(" ");
            }
        }
    }

    const splitWidth = starWidth / split;
    const classes = useStyles({ theme, starWidth, starHeight, splitWidth });
    const clearStarRatingLabel =
        (surveyTheme && translate(surveyTheme, "StarRatingClear", language, "Clear star rating")) ||
        "Clear star rating";

    const isHybridGridStarSection = pageQuestion?.type === QuestionType.HybridGrid;

    const hybridStarGridAttrs =
        isHybridGridStarSection &&
        gridRendering &&
        typeof gridRenderedColumnIndex === "number" &&
        typeof gridRenderedRowIndex === "number"
            ? questionId
                ? ({
                      "data-star-rating-grid-col": String(gridRenderedColumnIndex),
                      "data-star-rating-grid-row": String(gridRenderedRowIndex),
                      "data-star-rating-grid-qid": questionId,
                  } as const)
                : ({
                      "data-star-rating-grid-col": String(gridRenderedColumnIndex),
                      "data-star-rating-grid-row": String(gridRenderedRowIndex),
                  } as const)
            : null;

    const { elementReference, onKeyDown: onClearKeyDown } = useKeyboardNavigation(
        `${elementId}_CLEAR`,
        variable,
        question,
        StarRatingNavigation,
        null,
    );

    /** If user mousedowns on Clear then releases outside (cancel), focus never fires and the ref would stay true. */
    React.useEffect(() => {
        if (isDisabled) return;
        const onMouseUpCapture = (e: MouseEvent) => {
            if (!pointerDownOnClearRef.current) return;
            const clearBtn = elementReference.current as HTMLElement | null;
            if (!clearBtn) {
                pointerDownOnClearRef.current = false;
                return;
            }
            const t = e.target;
            if (t instanceof Node && !clearBtn.contains(t)) {
                pointerDownOnClearRef.current = false;
            }
        };
        document.addEventListener("mouseup", onMouseUpCapture, true);
        return () => document.removeEventListener("mouseup", onMouseUpCapture, true);
    }, [isDisabled]);

    function handleRadiogroupFocus(e: React.FocusEvent<HTMLDivElement>) {
        if (isDisabled) return;
        const root = e.currentTarget;
        const target = e.target as HTMLElement;
        const clearEl = elementReference.current as HTMLButtonElement | null;
        const rel = e.relatedTarget as Node | null;

        if (
            clearEl &&
            pointerDownOnClearRef.current &&
            (target === clearEl || clearEl.contains(target))
        ) {
            pointerDownOnClearRef.current = false;
            return;
        }

        if (rel && root.contains(rel)) {
            return;
        }

        const cameFromOutside = Boolean(rel && !root.contains(rel));

        if (!cameFromOutside) {
            return;
        }

        const redirectFromClear =
            clearEl != null && (target === clearEl || clearEl.contains(target));
        if (!redirectFromClear) {
            return;
        }

        const firstStar = resolveFirstSequentialStarInColumn(root) as HTMLElement | null;
        if (firstStar) {
            firstStar.focus();
        }
    }

    /** In a hybrid grid column, clear is on every row but Tab should enter at row 0’s first star (same column). */
    function resolveFirstSequentialStarInColumn(
        radiogroupRoot: HTMLDivElement,
    ): HTMLElement | null {
        if (
            pageQuestion?.type === QuestionType.HybridGrid &&
            gridRendering &&
            typeof gridRenderedColumnIndex === "number" &&
            typeof gridRenderedRowIndex === "number"
        ) {
            const doc = radiogroupRoot.ownerDocument;
            const qidPrefix =
                questionId != null && questionId !== ""
                    ? `[data-star-rating-grid-qid="${escapeCssAttributeSelectorValue(questionId)}"]`
                    : "";
            const columnScoped = doc.querySelector(
                `${qidPrefix}[data-star-rating-grid-col="${gridRenderedColumnIndex}"][data-star-rating-grid-row="0"] [role="radio"]`,
            );
            if (columnScoped instanceof HTMLElement) {
                return columnScoped;
            }
            return null;
        }
        return radiogroupRoot.querySelector('[role="radio"]') as HTMLElement | null;
    }

    return (
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        <div
            id={elementId}
            className={`${classnames(classes.starRating)} ${isDisabled ? classes.disabled : ""}`}
            onMouseOut={isDisabled ? undefined : () => setHoveredStarValue(0)}
            onMouseDownCapture={
                isDisabled
                    ? undefined
                    : (e) => {
                          if (e.button !== 0) return;
                          const clearBtn = elementReference.current as HTMLElement | null;
                          if (clearBtn && e.target instanceof Node && clearBtn.contains(e.target)) {
                              pointerDownOnClearRef.current = true;
                          }
                      }
            }
            onFocus={handleRadiogroupFocus}
            role="radiogroup"
            aria-invalid={modern && hasMainVariableValidationError ? true : undefined}
            {...(hybridStarGridAttrs ?? {})}
            {...ariaAttributes}
        >
            <div className="flex" />
            {getStars(props).map((index) => {
                const starValue = (index + 1) / split;

                const spacer = (
                    <div
                        className={`${classnames(classes.starRatingSpacer)} ${
                            isDisabled ? classes.disabled : ""
                        }`}
                        onClick={isDisabled ? undefined : () => setValue(starValue)}
                        onMouseOver={isDisabled ? undefined : () => setHoveredStarValue(starValue)}
                    />
                );

                return (
                    <React.Fragment key={index}>
                        {index % split === 0 && spacer}
                        <StarRenderer
                            index={index}
                            setValue={setValue}
                            value={value}
                            elementId={elementId}
                            split={split}
                            starWidth={starWidth}
                            starHeight={starHeight}
                            isDisabled={isDisabled}
                            variable={variable}
                            rerenderParentCallback={rerenderParentCallback}
                            hoveredStarValue={hoveredStarValue}
                            gridRendering={gridRendering}
                            gridRenderedRowIndex={gridRenderedRowIndex}
                            question={undefined}
                            questionnaire={questionnaire}
                            theme={surveyTheme}
                            language={language}
                            hasError={hasMainVariableValidationError}
                        />
                        {index % split === split - 1 && spacer}
                    </React.Fragment>
                );
            })}
            <div className="flex" />
            <Button
                buttonElementRef={elementReference}
                buttonType="CLEARSTARRATING"
                className={modern ? classes.clearButtonModern : undefined}
                icon={modern ? undefined : ["fas", "times"]}
                customIcon={modern ? <ModernStarRatingClearIcon size={16} /> : undefined}
                size={16}
                text={clearStarRatingLabel}
                displayText={false}
                onClick={isDisabled ? undefined : () => setValue(null, true)}
                disabled={isDisabled === true}
                onKeyDownEventHandler={onClearKeyDown}
            />
        </div>
    );
}
